import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Shield, Lock, DeviceMobile, Camera, Calculator,
  FileArrowDown, Heart, Leaf,
} from "@phosphor-icons/react";
import { startDirectCheckout } from "@/lib/api";
import { toast } from "sonner";
import TaxCalculatorWidget from "@/components/TaxCalculatorWidget";
import InstallGuideModal from "@/components/InstallGuideModal";

const fmtCAD = (v) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 })
    .format(Number(v) || 0);

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthed, loading } = useAuth();

  // The current calculator estimate is lifted here so the pricing section can
  // reference it dynamically. { credit, qualifies, hasInputs }.
  const [estimate, setEstimate] = useState({ credit: 0, qualifies: false, hasInputs: false });

  // Memoised so TaxCalculatorWidget's useEffect doesn't re-fire on every
  // Landing re-render.
  const handleEstimateChange = useCallback((snap) => setEstimate(snap), []);

  useEffect(() => {
    if (!loading && isAuthed) navigate("/log");
  }, [isAuthed, loading, navigate]);

  const goSubscribe = async (plan = "yearly") => {
    try {
      await startDirectCheckout(plan);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Try again in a moment.");
    }
  };

  const scrollToCalculator = () => {
    const el = document.getElementById("calculator");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToPricing = () => {
    const el = document.getElementById("pricing");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div>
      {/* ================================================================== */}
      {/* 1. HERO                                                            */}
      {/* ================================================================== */}
      <section className="min-h-screen relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/37840040/pexels-photo-37840040.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E2420]/85 via-[#1E2420]/55 to-[#2C4C3B]/40" />

        <div className="relative z-10 min-h-screen flex flex-col">
          {/* Top nav */}
          <header className="px-6 sm:px-10 py-6 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <div className="bg-[#F7F6F2] rounded-2xl px-3 py-2 shadow-lg">
                <img
                  src="/medeasy-logo.png"
                  alt="MedEasy — Medical Expenses & KM Tracker · Canada"
                  className="h-14 sm:h-16 w-auto object-contain"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <InstallGuideModal
                trigger={
                  <Button
                    data-testid="landing-install-app"
                    variant="outline"
                    className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white backdrop-blur"
                  >
                    <DeviceMobile size={16} weight="duotone" className="mr-1.5" />
                    Get app
                  </Button>
                }
              />
              <Button
                data-testid="landing-nav-cta"
                onClick={scrollToCalculator}
                className="hidden sm:inline-flex rounded-full bg-[#D4A373] text-[#1E2420] hover:bg-[#E5B687] font-semibold"
              >
                See My Free Estimate
              </Button>
              <Button
                data-testid="landing-signin"
                onClick={() => navigate("/login")}
                variant="outline"
                className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white backdrop-blur"
              >
                Sign in
              </Button>
            </div>
          </header>

          {/* Hero content */}
          <div className="flex-1 px-6 sm:px-10 lg:px-20 flex items-center">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-white/90 text-xs uppercase tracking-[0.2em] mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4A373]" />
                Built for Canadians • CRA-friendly
              </div>

              <h1 className="font-[Manrope] font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight max-w-3xl">
                Claim every medical tax credit your household is owed.
              </h1>

              <p className="text-white/90 text-lg sm:text-xl mt-6 max-w-2xl leading-relaxed">
                From medical travel over 40 km to insurance premiums, dental, and physio, MedEasy tracks every eligible expense so your medical tax credit is complete and CRA-ready.
              </p>

              {/* H&R Block cited trust anchor */}
              <p
                data-testid="landing-trust-anchor"
                className="text-white/75 text-sm sm:text-base mt-5 max-w-2xl leading-relaxed"
              >
                H&amp;R Block&apos;s 2025 review of Canadians who filed elsewhere found an average of about <span className="text-[#D4A373] font-semibold">$2,900</span> in missed credits and benefits — medical expenses among the most commonly overlooked. MedEasy makes sure your medical receipts aren&apos;t part of what slips through.
              </p>

              <div className="mt-10">
                <Button
                  data-testid="landing-cta-calculator"
                  onClick={scrollToCalculator}
                  size="lg"
                  className="rounded-full h-14 px-8 bg-[#2C4C3B] hover:bg-[#3A634D] text-white text-base sm:text-lg font-semibold shadow-lg shadow-[#2C4C3B]/30"
                >
                  See My Free Estimate
                  <ArrowRight size={20} className="ml-2" weight="bold" />
                </Button>
              </div>

              <div
                data-testid="landing-cta-subtext"
                className="mt-3 text-xs sm:text-sm text-white/75"
              >
                MedEasy is $49.50/year · takes 10 seconds to check if it&apos;s worth it.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* 2. CALCULATOR                                                      */}
      {/* ================================================================== */}
      <div id="calculator" className="scroll-mt-16">
        <TaxCalculatorWidget onEstimateChange={handleEstimateChange} />
      </div>

      {/* ================================================================== */}
      {/* 3. BENEFIT SCREEN — "Never lose a receipt again" (green tint)      */}
      {/* ================================================================== */}
      <BenefitScreen
        testId="benefit-receipts"
        tint="#E8F1E8"
        Icon={Camera}
        headline="Never lose a receipt again."
        body="Snap or tap a photo. That&rsquo;s it — we tag, total, and file it under the right CRA category."
      />

      {/* ================================================================== */}
      {/* 4. BENEFIT SCREEN — "Every CRA rule, handled" (warm sand tint)     */}
      {/* ================================================================== */}
      <BenefitScreen
        testId="benefit-rules"
        tint="#F5EEE0"
        Icon={Calculator}
        headline="Every CRA rule, handled."
        body="40 km and 80 km travel tiers are auto-calculated the moment you scan an appointment letter."
      />

      {/* ================================================================== */}
      {/* 5. BENEFIT SCREEN — "Audit-ready in one tap" (light mint/white)    */}
      {/* ================================================================== */}
      <BenefitScreen
        testId="benefit-export"
        tint="#F1F7F2"
        Icon={FileArrowDown}
        headline="Audit-ready in one tap."
        body="Export one clean PDF or CSV summary at tax time — hand it to your accountant, or file it yourself."
      />

      {/* ================================================================== */}
      {/* 6. EMPATHY — "We know why you stopped tracking"                    */}
      {/*    (uncited "thousands unclaimed" sentence removed per spec)       */}
      {/* ================================================================== */}
      <section
        data-testid="landing-friction"
        className="bg-[#F7F6F2] py-16 sm:py-24 px-6 sm:px-10 lg:px-20"
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#5C6660] font-semibold mb-4">
            We hear you
          </div>
          <h2 className="font-[Manrope] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#1E2420] leading-tight tracking-tight">
            We know why you stopped tracking.
          </h2>
          <p className="text-lg text-[#3D4540] leading-relaxed mt-5 max-w-3xl">
            It&rsquo;s not laziness, it&rsquo;s cognitive overload. When you&rsquo;re managing appointments, symptoms, and daily life, your brain naturally drops &lsquo;admin&rsquo; tasks to protect your energy. MedEasy removes that burden entirely.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                title: "The glovebox logbook",
                body: "Forgetting to write down odometer readings and exact doctor addresses on long specialist drives.",
                testid: "friction-card-logbook",
              },
              {
                title: "The crumpled receipts",
                body: "Losing track of small pharmacy co-pays, dental receipts, and eyeglasses that stack up fast over 12 months.",
                testid: "friction-card-receipts",
              },
              {
                title: "The CRA rules headache",
                body: "Trying to figure out complex 40km and 80km travel thresholds when you&rsquo;re already exhausted.",
                testid: "friction-card-rules",
              },
            ].map((c) => (
              <div
                key={c.testid}
                data-testid={c.testid}
                className="rounded-2xl bg-white border border-[#E3E1D9] p-6 sm:p-7 shadow-[0_2px_20px_rgba(30,36,32,0.04)] flex flex-col"
              >
                <h3 className="font-[Manrope] font-semibold text-lg sm:text-xl text-[#1E2420] leading-snug">
                  {c.title}
                </h3>
                <p
                  className="text-base text-[#3D4540] leading-relaxed mt-3"
                  dangerouslySetInnerHTML={{ __html: c.body }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* 7. FOUNDER QUOTE — short, one sentence                             */}
      {/* ================================================================== */}
      <section
        className="bg-white py-16 sm:py-20 px-6 sm:px-10 lg:px-20"
        data-testid="founders-note"
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#5C6660] font-semibold mb-5">
            A note from the creator
          </div>
          <div className="flex items-start gap-5">
            <div
              data-testid="founder-avatar-icon"
              className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#2C4C3B] flex items-center justify-center shadow-sm relative"
              aria-label="MedEasy creator icon"
            >
              <Heart size={30} weight="fill" className="text-[#C17767]" />
              <Leaf size={16} weight="fill" className="text-white absolute bottom-2 right-2 drop-shadow" />
            </div>
            <blockquote className="flex-1">
              <p className="font-[Manrope] text-xl sm:text-2xl lg:text-3xl leading-[1.35] tracking-tight text-[#1E2420]">
                <span className="text-[#D4A373] font-bold">&ldquo;</span>
                I&rsquo;ve lived with MS for 15 years — I built MedEasy so you don&rsquo;t need to become a medical tax expert to claim what your household is owed.
                <span className="text-[#D4A373] font-bold">&rdquo;</span>
              </p>
              <footer className="mt-4 text-sm text-[#5C6660]">
                &mdash; The creator of MedEasy, himself an MS warrior
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* 8. PRICING — dynamic headline tied to the calculator result        */}
      {/* ================================================================== */}
      <section
        id="pricing"
        data-testid="landing-pricing"
        className="bg-[#F7F6F2] py-20 sm:py-28 px-6 sm:px-10 lg:px-20 scroll-mt-16"
      >
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C4C3B]/8 text-[#2C4C3B] text-[10px] uppercase tracking-[0.22em] font-bold">
            Real price · no fine print
          </div>

          {estimate.qualifies && estimate.credit > 0 ? (
            <h2
              data-testid="pricing-headline-dynamic"
              className="font-[Manrope] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#1E2420] mt-5 leading-tight tracking-tight"
            >
              Your calculator said{" "}
              <span className="text-[#2C4C3B]" data-testid="pricing-estimate-value">
                {fmtCAD(estimate.credit)}
              </span>.
              <br className="hidden sm:block" />
              MedEasy costs <span className="text-[#2C4C3B]">$49.50/year</span>.
            </h2>
          ) : (
            <h2
              data-testid="pricing-headline-static"
              className="font-[Manrope] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#1E2420] mt-5 leading-tight tracking-tight"
            >
              MedEasy costs <span className="text-[#2C4C3B]">$49.50/year</span>.
            </h2>
          )}

          <p className="text-lg text-[#3D4540] leading-relaxed mt-5 max-w-2xl mx-auto">
            If your estimate clears the price, it&rsquo;s already paid for itself — before you&rsquo;ve logged a single receipt.
          </p>

          <div
            className="mt-10 inline-flex flex-col items-center"
            data-testid="pricing-block"
          >
            <div className="font-[Manrope] font-bold text-6xl sm:text-7xl text-[#1E2420] leading-none tracking-tight">
              $49.50<span className="text-2xl sm:text-3xl text-[#5C6660] font-semibold">/year</span>
            </div>
            <div className="mt-2 text-sm text-[#5C6660]">
              no free trial · cancel anytime
            </div>

            <Button
              data-testid="pricing-cta"
              onClick={() => goSubscribe("yearly")}
              size="lg"
              className="mt-6 rounded-full h-14 px-8 bg-[#2C4C3B] hover:bg-[#3A634D] text-white font-semibold text-base sm:text-lg shadow-lg shadow-[#2C4C3B]/30"
            >
              Get MedEasy — $49.50/year
              <ArrowRight size={20} className="ml-2" weight="bold" />
            </Button>

            {/* Hope Air demoted to a plain text line AFTER the CTA */}
            <div
              data-testid="landing-hope-air-line"
              className="mt-4 text-sm text-[#5C6660]"
            >
              10% of profits go to Hope Air.
            </div>
          </div>

          <p
            data-testid="pricing-legal"
            className="text-xs text-[#5C6660] italic mt-10 leading-relaxed max-w-2xl mx-auto"
          >
            MedEasy is a tracking tool, not a tax preparer. Your actual credit depends on your return, income, and receipts.
          </p>
        </div>
      </section>

      {/* ================================================================== */}
      {/* 9. FOOTER — trust badges + legal + demoted links                   */}
      {/* ================================================================== */}
      <footer
        className="bg-[#1E2420] text-white/70 py-12 px-6 sm:px-10 lg:px-20"
        data-testid="landing-footer"
      >
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row gap-8 sm:items-start sm:justify-between">
          <div className="flex-1">
            <div
              data-testid="landing-trust-badges-footer"
              className="flex flex-wrap items-center gap-2 sm:gap-3"
            >
              {[
                { Icon: Lock,   label: "256-bit AES Security" },
                { Icon: Leaf,   label: "Data Stored in Canada" },
                { Icon: Shield, label: "Full CRA Compliance" },
              ].map(({ Icon, label }) => (
                <div
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/15 px-3.5 py-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.14em]"
                >
                  <Icon size={14} weight="duotone" className="text-[#D4A373]" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
            <p className="text-xs mt-6 leading-relaxed max-w-xl">
              Made for Canadian patients &amp; caregivers. Your data stays yours. MedEasy is a tracking tool, not a tax preparer.
            </p>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <button
              data-testid="landing-footer-have-code"
              onClick={() => navigate("/redeem")}
              className="hover:text-white underline underline-offset-4 decoration-[#D4A373]/60 hover:decoration-[#D4A373]"
            >
              Have a gift or promo code?
            </button>
            <button
              data-testid="landing-footer-hope-air"
              onClick={() => navigate("/hope-air")}
              className="hover:text-white underline underline-offset-4"
            >
              Hope Air partnership
            </button>
            <button
              data-testid="landing-footer-terms"
              onClick={() => navigate("/terms")}
              className="hover:text-white underline underline-offset-4"
            >
              Terms
            </button>
            <button
              data-testid="landing-footer-privacy"
              onClick={() => navigate("/privacy")}
              className="hover:text-white underline underline-offset-4"
            >
              Privacy
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}


/* ==================================================================== */
/* BenefitScreen — single-idea-per-screen (Headspace rhythm)            */
/* One icon + one headline + one line. Only the tint background varies. */
/* ==================================================================== */
function BenefitScreen({ testId, tint, Icon, headline, body }) {
  return (
    <section
      data-testid={testId}
      className="py-20 sm:py-28 px-6 sm:px-10 lg:px-20"
      style={{ backgroundColor: tint }}
    >
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#2C4C3B] shadow-lg shadow-[#2C4C3B]/20">
          <Icon size={32} weight="duotone" className="text-white" />
        </div>
        <h2 className="font-[Manrope] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#1E2420] mt-7 leading-tight tracking-tight">
          {headline}
        </h2>
        <p className="text-lg sm:text-xl text-[#3D4540] mt-4 leading-relaxed max-w-2xl mx-auto">
          {body}
        </p>
      </div>
    </section>
  );
}
